<template>
  <div>
    <v-card dark class="create-post-card">
      <v-toolbar flat dark color="transparent">
        <v-btn icon @click="resetAndClose"><v-icon>mdi-close</v-icon></v-btn>
        <v-toolbar-title class="font-weight-bold">Neuer Beitrag</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          v-if="postType && postType !== 'training_partner_search'"
          text
          class="font-weight-bold"
          color="primary"
          :loading="isSubmitting"
          :disabled="!isFormValid"
          @click="submitPost">
          Posten
        </v-btn>
      </v-toolbar>

      <div v-if="!postType" class="pa-4 pa-md-8">
        <h2 class="text-h5 font-weight-bold text-center mb-8">Was möchtest du heute teilen?</h2>
        <div class="post-type-grid">
          <div class="post-type-card" @click="selectPostType('image_post')">
            <v-icon size="48" color="primary">mdi-image-plus-outline</v-icon>
            <span class="card-title">Bild / Foto</span>
            <p class="card-subtitle">Zeig dein bestes Gym-Selfie.</p>
          </div>
          <div class="post-type-card" @click="selectPostType('new_pr')">
            <v-icon size="48" color="#f7b5a7">mdi-trophy-award</v-icon>
            <span class="card-title">Neuer PR</span>
            <p class="card-subtitle">Feiere einen neuen Rekord.</p>
          </div>
          <div class="post-type-card" @click="selectPostType('status_update')">
            <v-icon size="48" color="#a7f7b5">mdi-pencil-box-multiple-outline</v-icon>
            <span class="card-title">Status Update</span>
            <p class="card-subtitle">Teile deine Gedanken mit.</p>
          </div>
          <div class="post-type-card" @click="selectPostType('training_partner_search')">
            <v-icon size="48" color="#a7dff7">mdi-account-multiple-plus-outline</v-icon>
            <span class="card-title">Partner finden</span>
            <p class="card-subtitle">Starte eine Suche im Studio.</p>
          </div>
        </div>
        <v-file-input ref="fileInput" v-model="originalImageFile" accept="image/*" class="d-none" hide-input @change="openEditor"></v-file-input>
      </div>

      <div v-if="postType && postType !== 'training_partner_search'" class="pa-4 pa-md-6">
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-row v-if="postType === 'image_post'">
              <v-col cols="12" md="7">
                <v-img v-if="previewUrl" :src="previewUrl" class="rounded-lg elevation-4" max-height="450" contain></v-img>
              </v-col>
              <v-col cols="12" md="5">
                <v-textarea
                  v-model="content.text"
                  label="Beschreibe dein Bild..."
                  dark
                  filled
                  rounded
                  auto-grow
                  rows="4"
                  counter="500"
                  :rules="[rules.maxLength(500)]"></v-textarea>
                <v-divider class="my-4"></v-divider>
                <v-subheader>Sichtbarkeit</v-subheader>
                <v-radio-group v-model="audience" row dark mandatory>
                  <v-radio value="friends" label="Nur Freunde"></v-radio>
                  <v-radio value="studio" label="Ganzes Studio"></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>

            <div v-else>
              <v-textarea v-model="content.text" :label="textLabel" dark filled rounded counter="500" :rules="[rules.maxLength(500)]"></v-textarea>
              <div v-if="postType === 'new_pr'">
                <v-text-field v-model="content.pr.exercise" label="Übung" dark filled rounded :rules="[rules.required]"></v-text-field>
                <v-text-field
                  v-model="content.pr.value"
                  label="Gewicht, Distanz oder Zeit"
                  dark
                  filled
                  rounded
                  class="mt-2"
                  :rules="[rules.required]"></v-text-field>
              </div>
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn text @click="postType = null">Zurück</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </div>

      <div v-if="postType === 'training_partner_search'" class="wizard-container">
        <v-progress-linear :value="(wizardStep / 3) * 100" color="primary" height="6" class="mb-2"></v-progress-linear>

        <v-window v-model="wizardStep">
          <v-window-item :value="1">
            <div class="wizard-content">
              <h3 class="wizard-title">Dein<br />Workout</h3>
              <p class="wizard-subtitle">Beschreibe dein geplantes Training.</p>
              <v-row>
                <v-col cols="12" md="7">
                  <h4 class="wizard-col-title">Trainingsart</h4>
                  <v-chip-group v-model="content.search.workoutType" active-class="primary--text" column>
                    <v-chip v-for="item in options.workoutTypes" :key="item" :value="item" class="wizard-chip" filter outlined large>{{
                      item
                    }}</v-chip>
                  </v-chip-group>
                </v-col>
                <v-col cols="12" md="5">
                  <h4 class="wizard-col-title">Intensität</h4>
                  <v-chip-group v-model="content.search.intensity" active-class="primary--text" mandatory column>
                    <v-chip v-for="item in options.intensities" :key="item" :value="item" class="wizard-chip" filter outlined large>{{
                      item
                    }}</v-chip>
                  </v-chip-group>
                </v-col>
              </v-row>
            </div>
          </v-window-item>

          <v-window-item :value="2">
            <div class="wizard-content">
              <h3 class="wizard-title">Deine<br />Verfügbarkeit</h3>
              <p class="wizard-subtitle">Wann passt es dir am besten?</p>
              <v-row>
                <v-col cols="12" md="7">
                  <h4 class="wizard-col-title">Tage</h4>
                  <v-chip-group v-model="content.search.availability.days" active-class="primary--text" column multiple>
                    <v-chip v-for="day in options.days" :key="day" :value="day" class="wizard-chip" filter outlined large>{{ day }}</v-chip>
                  </v-chip-group>
                </v-col>
                <v-col cols="12" md="5">
                  <h4 class="wizard-col-title">Tageszeit</h4>
                  <v-chip-group v-model="content.search.availability.timeOfDay" active-class="primary--text" mandatory column>
                    <v-chip v-for="time in options.times" :key="time" :value="time" class="wizard-chip" filter outlined large>{{ time }}</v-chip>
                  </v-chip-group>
                </v-col>
              </v-row>
            </div>
          </v-window-item>

          <v-window-item :value="3">
            <div class="wizard-content">
              <h3 class="wizard-title">Letzte<br />Details</h3>
              <p class="wizard-subtitle">Füge eine Nachricht hinzu und wähle die Sichtbarkeit.</p>
              <v-textarea
                v-model="content.text"
                label="Deine Nachricht (optional)"
                placeholder="z.B. Suche jemanden für Bankdrücken..."
                dark
                filled
                rounded
                auto-grow
                rows="3"></v-textarea>
              <v-subheader class="mt-4">Sichtbarkeit des Posts</v-subheader>
              <v-radio-group v-model="audience" row dark mandatory>
                <v-radio value="friends" label="Nur Freunde"></v-radio>
                <v-radio value="studio" label="Ganzes Studio"></v-radio>
              </v-radio-group>
              <p class="caption grey--text">
                Wenn du "Ganzes Studio" wählst, können auch Mitglieder, mit denen du nicht befreundet bist, eine Trainingsanfrage senden.
              </p>
            </div>
          </v-window-item>
        </v-window>

        <div class="wizard-navigation pa-4">
          <v-btn text @click="wizardStep > 1 ? wizardStep-- : (postType = null)">
            {{ wizardStep > 1 ? "Zurück" : "Abbrechen" }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn v-if="wizardStep < 3" color="primary" rounded @click="wizardStep++" :disabled="!isWizardStepValid">Weiter</v-btn>
          <v-btn v-else color="primary" rounded :loading="isSubmitting" :disabled="!isFormValid" @click="submitPost">Suche posten</v-btn>
        </div>
      </div>
    </v-card>

    <ImageEditor
      v-model="editorOpen"
      :image-file="originalImageFile"
      @save="onImageEdited"
      @closeWithoutReset="editorOpen = false"
      @close="closeEditor" />
  </div>
</template>

<script>
import api from "@/api"
import ImageEditor from "../../ImageEditor.vue"

export default {
  components: {
    ImageEditor,
  },
  data() {
    return {
      wizardStep: 1,
      valid: false,
      postType: null,
      isSubmitting: false,
      editorOpen: false,
      audience: "friends",
      originalImageFile: null,
      editedImageFile: null,
      previewUrl: null,
      content: {
        text: "",
        imageIdentifier: "",
        pr: { exercise: "", value: "" },
        search: {
          workoutType: null,
          intensity: "Fordernd",
          availability: {
            type: "recurring",
            days: [],
            timeOfDay: "Abends",
          },
        },
      },
      options: {
        workoutTypes: ["Krafttraining", "Cardio", "Crossfit", "Yoga", "Beweglichkeit"],
        intensities: ["Locker & leicht", "Fordernd", "All-In!"],
        days: ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"],
        times: ["Morgens", "Mittags", "Nachmittags", "Abends"],
      },
      rules: {
        required: (value) => !!value || "Dieses Feld ist erforderlich.",
        maxLength: (max) => (value) => !value || value.length <= max || `Maximal ${max} Zeichen.`,
      },
    }
  },
  computed: {
    textLabel() {
      const labels = {
        status_update: "Was gibt's Neues?",
        new_pr: "Erzähl mehr über deinen Rekord!",
      }
      return labels[this.postType] || "Dein Text"
    },
    isFormValid() {
      if (this.postType === "training_partner_search") {
        return (
          this.content.search.workoutType &&
          this.content.search.intensity &&
          this.content.search.availability.days.length > 0 &&
          this.content.search.availability.timeOfDay
        )
      }
      if (this.postType === "image_post") {
        return !!this.editedImageFile
      }
      return this.valid
    },
    isWizardStepValid() {
      if (this.wizardStep === 1) {
        return this.content.search.workoutType && this.content.search.intensity
      }
      if (this.wizardStep === 2) {
        return this.content.search.availability.days.length > 0 && this.content.search.availability.timeOfDay
      }
      return true
    },
  },
  methods: {
    resetAndClose() {
      this.$emit("close")
      setTimeout(() => {
        this.wizardStep = 1
        this.postType = null
        this.originalImageFile = null
        this.editedImageFile = null
        this.previewUrl = null
        this.content = {
          text: "",
          imageIdentifier: "",
          pr: { exercise: "", value: "" },
          search: {
            workoutType: null,
            intensity: "Fordernd",
            availability: {
              type: "recurring",
              days: [],
              timeOfDay: "Abends",
            },
          },
        }
      }, 300)
    },
    selectPostType(type) {
      if (type === "image_post") {
        // Nur den Datei-Input klicken. Der Rest wird vom @change-Event erledigt.
        this.$refs.fileInput.$el.querySelector("input").click()
      } else {
        this.postType = type
      }
    },
    closeEditor() {
      this.originalImageFile = null
      this.editedImageFile = null
      this.previewUrl = null
      this.editorOpen = false
      this.postType = null // Zurück zur Typ-Auswahl
    },
    openEditor(file) {
      if (!file) {
        // Wenn der User im Datei-Dialog auf "Abbrechen" klickt,
        // bleiben wir auf der Auswahl-Seite.
        this.postType = null
        return
      }
      // Wenn eine Datei ausgewählt wurde, setzen wir den Typ und öffnen den Editor.
      this.postType = "image_post"
      this.originalImageFile = file
      this.editorOpen = true
    },
    onImageEdited(editedFile) {
      this.editedImageFile = editedFile
      this.previewUrl = URL.createObjectURL(editedFile)
      this.editorOpen = false
    },
    async submitPost() {
      if (!this.isFormValid) {
        if (this.postType !== "training_partner_search") {
          this.$refs.form.validate()
        }
        return
      }

      this.isSubmitting = true
      try {
        let finalContent = {}

        if (this.postType === "image_post") {
          const uploadResponse = await api.social.uploadImage(this.editedImageFile)
          if (!uploadResponse.success) throw new Error(uploadResponse.message || "Bild-Upload fehlgeschlagen.")
          finalContent = { text: this.content.text, imageIdentifier: uploadResponse.identifier }
        } else if (this.postType === "new_pr") {
          finalContent = { text: this.content.text, pr: this.content.pr }
        } else if (this.postType === "status_update") {
          finalContent = { text: this.content.text }
        } else if (this.postType === "training_partner_search") {
          finalContent = { text: this.content.text, search: this.content.search }
        }

        const postData = {
          type: this.postType,
          content: finalContent,
          audience: this.audience,
        }

        await api.social.createPost(postData)
        this.$emit("post-created")
        this.resetAndClose()
      } catch (error) {
        console.error("Fehler beim Erstellen des Posts:", error)
        alert(error.message || "Der Post konnte nicht erstellt werden.")
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.create-post-card {
  border-radius: 16px !important;
  background-color: #232929;
}

/* Auswahl der Post-Typen */
.post-type-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.post-type-card {
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: translateY(-5px);
    background-color: rgba(255, 255, 255, 0.1);
    border-color: #f7b5a7;
  }

  .card-title {
    display: block;
    font-weight: 700;
    font-size: 1.1rem;
    margin-top: 16px;
    color: #fff;
  }

  .card-subtitle {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
    margin-top: 4px;
    margin-bottom: 0;
  }
}

/* Wizard für Partnersuche */
.wizard-container {
  padding: 0;
}
.wizard-content {
  padding: 16px 24px;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.wizard-title {
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1.1;
  color: #f7f7f4;
  margin-bottom: 8px;
}
.wizard-subtitle {
  color: rgba(247, 247, 244, 0.7);
  margin-bottom: 24px;
}
.wizard-col-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
}
.wizard-chip {
  transition: all 0.2s ease-in-out;
  border-width: 2px !important;
  font-weight: 600 !important;
  padding: 20px 24px !important;
  height: auto !important;
  border-radius: 12px !important;

  &:not(.v-chip--active) {
    background-color: rgba(255, 255, 255, 0.05) !important;
  }
}
.v-chip-group .v-chip--active {
  border-color: #f7b5a7 !important;
  background-color: rgba(247, 181, 167, 0.1) !important;
}
.wizard-navigation {
  display: flex;
  align-items: center;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
</style>
